import React, { useState } from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, IconButton, Typography, useTheme, alpha } from '@mui/material';
import { FileText, Download, Eye, Plus } from 'lucide-react';
import { Message } from '../../../types';
import { AttachmentDialog } from '../AttachmentDialog';

interface MessageContentProps {
  message: Message;
  isCurrentUser: boolean;
}

export const MessageContent: React.FC<MessageContentProps> = ({
  message,
  isCurrentUser,
}) => {
  const theme = useTheme();
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<Message['attachments'][0] | null>(null);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleAddToCase = (attachment: Message['attachments'][0]) => {
    setSelectedAttachment(attachment);
    setAttachmentDialogOpen(true);
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: isCurrentUser ? 'primary.main' : 'background.default',
          color: isCurrentUser ? 'primary.contrastText' : 'text.primary',
          p: 2,
          borderRadius: 2,
          boxShadow: 1,
          width: '100%',
        }}
      >
        {message.content && (
          <Box 
            component="div" 
            sx={{ 
              mb: message.attachments ? 2 : 0,
              whiteSpace: 'pre-wrap',
            }}
          >
            {message.content}
          </Box>
        )}

        {message.attachments && message.attachments.length > 0 && (
          <List disablePadding>
            {message.attachments.map((attachment) => (
              <ListItem
                key={attachment.id}
                sx={{
                  px: 0,
                  py: 1,
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <FileText size={18} />
                  </ListItemIcon>
                  <ListItemText
                    primary={attachment.name}
                    secondary={formatFileSize(attachment.size)}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { 
                        color: isCurrentUser ? 'inherit' : 'text.primary',
                      }
                    }}
                    secondaryTypographyProps={{
                      sx: { 
                        color: isCurrentUser 
                          ? alpha(theme.palette.primary.contrastText, 0.7)
                          : 'text.secondary',
                      }
                    }}
                  />
                </Box>
                <Box 
                  sx={{ 
                    display: 'flex',
                    gap: 1,
                    pl: 5, // Align with text after icon
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleAddToCase(attachment)}
                    sx={{ 
                      color: isCurrentUser ? 'inherit' : 'primary.main',
                      bgcolor: isCurrentUser ? alpha('#fff', 0.1) : alpha(theme.palette.primary.main, 0.05),
                      '&:hover': {
                        bgcolor: isCurrentUser ? alpha('#fff', 0.2) : alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <Plus size={16} />
                  </IconButton>
                  <IconButton
                    size="small"
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      color: isCurrentUser ? 'inherit' : 'primary.main',
                      bgcolor: isCurrentUser ? alpha('#fff', 0.1) : alpha(theme.palette.primary.main, 0.05),
                      '&:hover': {
                        bgcolor: isCurrentUser ? alpha('#fff', 0.2) : alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <Eye size={16} />
                  </IconButton>
                  <IconButton
                    size="small"
                    href={attachment.url}
                    download
                    sx={{ 
                      color: isCurrentUser ? 'inherit' : 'primary.main',
                      bgcolor: isCurrentUser ? alpha('#fff', 0.1) : alpha(theme.palette.primary.main, 0.05),
                      '&:hover': {
                        bgcolor: isCurrentUser ? alpha('#fff', 0.2) : alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <Download size={16} />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {selectedAttachment && (
        <AttachmentDialog
          open={attachmentDialogOpen}
          onClose={() => {
            setAttachmentDialogOpen(false);
            setSelectedAttachment(null);
          }}
          attachment={selectedAttachment}
        />
      )}
    </>
  );
};